/**
 * ChatBot.js  —  v34.98 (Feb 13 2026)  ·  /chat-bot
 *
 * Unified Slack + Microsoft Teams bot landing page. Replaces the
 * Slack-only `/slack-bot` card on the Landing. Both bots are
 * pre-launch — this page is the lead-gen funnel that proves which
 * platform deserves engineering effort first.
 *
 * Why this page exists:
 *   - ~95% of Fortune 500 companies use Teams. Slack-only positioning
 *     loses our most likely paying customers.
 *   - Some hackathon viewers said the Slack install was confusing.
 *     This page treats both as equal and waitlist-only — no install
 *     friction at all.
 */
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { MessageSquare, Slack, Send, CheckCircle2, AlertTriangle, ArrowRight, Sparkles, Lock, Clock, Plug, Search, GitPullRequest, Bell } from 'lucide-react';
import SEOHead from '../components/SEOHead';
import PublicNavbar from '../components/PublicNavbar';
import LiveBotDemo from '../components/LiveBotDemo';

const API = process.env.REACT_APP_BACKEND_URL;

// ---------------------------------------------------------------------------
// One waitlist card per platform — Slack (violet) and Teams (sky/Microsoft blue)
// ---------------------------------------------------------------------------
const PlatformCard = ({ platform, count }) => {
  const isSlack = platform === 'slack';
  const cfg = isSlack
    ? {
        title: 'Slack',
        accent: 'violet',
        ring: 'ring-violet-500/30',
        bg: 'bg-violet-500/15',
        text: 'text-violet-300',
        border: 'border-violet-500/30',
        solid: 'bg-violet-500',
        solidHover: 'hover:bg-violet-400',
        focus: 'focus:border-violet-500/40',
        Icon: Slack,
        tagline: 'For dev-first teams · startup-friendly · 60M users',
        cmd: '/cartie why did my bill spike',
      }
    : {
        title: 'Microsoft Teams',
        accent: 'sky',
        ring: 'ring-sky-500/30',
        bg: 'bg-sky-500/15',
        text: 'text-sky-300',
        border: 'border-sky-500/30',
        solid: 'bg-sky-500',
        solidHover: 'hover:bg-sky-400',
        focus: 'focus:border-sky-500/40',
        Icon: MessageSquare,
        tagline: 'For enterprise · 95% of Fortune 500 · 320M users',
        cmd: '@Cartie why did my bill spike',
      };

  const [email, setEmail] = useState('');
  const [company, setCompany] = useState('');
  const [state, setState] = useState({ status: 'idle', message: '' });

  const submit = async (e) => {
    e.preventDefault();
    if (state.status === 'loading') return;
    setState({ status: 'loading', message: '' });
    try {
      const res = await axios.post(`${API}/api/chat-bot/waitlist`, {
        email: email.trim(),
        platform,
        company: company.trim() || null,
      });
      if (res.data.ok) {
        setState({ status: 'success', message: res.data.message });
      } else {
        setState({ status: 'error', message: res.data.error || 'Something went wrong.' });
      }
    } catch (err) {
      setState({ status: 'error', message: err.response?.data?.detail || err.message || 'Failed.' });
    }
  };

  return (
    <div
      className={`relative rounded-2xl border ${cfg.border} bg-white/[0.02] p-6 sm:p-8 flex flex-col`}
      data-testid={`chat-bot-card-${platform}`}
    >
      {/* Header */}
      <div className="flex items-center gap-3 mb-4">
        <div className={`inline-flex items-center justify-center w-12 h-12 rounded-xl ${cfg.bg} ring-1 ${cfg.ring} shadow-lg`}>
          <cfg.Icon className={`w-6 h-6 ${cfg.text}`} strokeWidth={2.2} />
        </div>
        <div>
          <h3 className="text-xl font-bold text-white">{cfg.title}</h3>
          <p className="text-[11px] text-slate-500">{cfg.tagline}</p>
        </div>
      </div>

      {/* Example command */}
      <div className="rounded-lg border border-white/10 bg-slate-950/60 px-3 py-2.5 mb-5 font-mono text-xs text-slate-300">
        <span className={cfg.text}>{cfg.cmd}</span>
        <div className="text-slate-500 text-[10px] mt-1">&rarr; &ldquo;Your OpenAI bill jumped 4&times;. The cause is a recursive loop in stream.py:42. Here&apos;s a PR with the fix.&rdquo;</div>
      </div>

      {/* Waitlist counter */}
      <div className="text-xs text-slate-400 mb-4 flex items-center gap-2">
        <span className="relative flex h-1.5 w-1.5">
          <span className={`animate-ping absolute inline-flex h-full w-full rounded-full opacity-70 ${cfg.text.replace('text', 'bg')}`} />
          <span className={`relative inline-flex rounded-full h-1.5 w-1.5 ${cfg.text.replace('text', 'bg')}`} />
        </span>
        <span><span className={`${cfg.text} font-semibold tabular-nums`}>{count}</span> teams already on the {cfg.title} waitlist</span>
      </div>

      {/* Form */}
      {state.status !== 'success' ? (
        <form onSubmit={submit} className="space-y-3 flex-1" data-testid={`chat-bot-form-${platform}`}>
          <input
            type="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="work@yourcompany.com"
            className={`w-full px-3 py-2.5 bg-slate-950/70 border border-white/10 rounded-lg text-white placeholder-slate-500 ${cfg.focus} focus:outline-none transition-colors text-sm`}
            data-testid={`chat-bot-email-${platform}`}
          />
          <input
            type="text"
            value={company}
            onChange={(e) => setCompany(e.target.value)}
            placeholder="Company (optional)"
            className={`w-full px-3 py-2.5 bg-slate-950/70 border border-white/10 rounded-lg text-white placeholder-slate-500 ${cfg.focus} focus:outline-none transition-colors text-sm`}
            data-testid={`chat-bot-company-${platform}`}
          />
          <button
            type="submit"
            disabled={state.status === 'loading'}
            className={`w-full inline-flex items-center justify-center gap-2 px-4 py-3 rounded-lg ${cfg.solid} ${cfg.solidHover} text-slate-950 font-semibold transition-colors disabled:opacity-60 disabled:cursor-wait`}
            data-testid={`chat-bot-submit-${platform}`}
          >
            {state.status === 'loading' ? (
              <>Submitting…</>
            ) : (
              <>
                <Send className="w-4 h-4" />
                Join {cfg.title} waitlist
              </>
            )}
          </button>
          {state.status === 'error' && (
            <div className="flex items-center gap-2 text-xs text-rose-300">
              <AlertTriangle className="w-3.5 h-3.5" />
              {state.message}
            </div>
          )}
          <p className="text-[10px] text-slate-500">No spam. One email when the bot ships. Unsubscribe anytime.</p>
        </form>
      ) : (
        <div className={`flex-1 flex flex-col items-center justify-center text-center p-6 rounded-xl ${cfg.bg.replace('/15', '/5')} border ${cfg.border}`} data-testid={`chat-bot-success-${platform}`}>
          <CheckCircle2 className={`w-10 h-10 ${cfg.text} mb-3`} />
          <p className="text-white font-semibold mb-1">{state.message}</p>
          <p className="text-xs text-slate-400">We&apos;ll email you the moment it ships. Promise.</p>
        </div>
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Examples of what you can ask
// ---------------------------------------------------------------------------
const EXAMPLES = [
  { ask: 'why did my AWS bill spike yesterday?', answer: 'You ran 4× more S3 GETs than usual — looks like an analytics job. I traced it to airflow/jobs/cohort_export.py.' },
  { ask: 'who spent the most on OpenAI this week?', answer: 'The ranking-service team — $4,210, up 38% from last week. 92% of it is GPT-4o on summarization. Want me to suggest Claude Haiku swap?' },
  { ask: 'what changed in our Snowflake cost?', answer: 'A new query in your ANALYTICS warehouse is scanning 14× more partitions than last week. Engineer: @priya · query ID: f3c2…' },
  { ask: 'forecast next month', answer: 'Based on the last 90 days, you\'ll spend $48.2K next month — 18% over budget. The driver is a new Databricks job. Want me to alert when it crosses $40K?' },
];

// ---------------------------------------------------------------------------
// Page shell
// ---------------------------------------------------------------------------
export default function ChatBot() {
  const [counts, setCounts] = useState({ slack: 0, teams: 0 });

  useEffect(() => {
    let cancelled = false;
    fetch(`${API}/api/chat-bot/waitlist/stats`)
      .then((r) => r.ok ? r.json() : null)
      .then((d) => { if (d && !cancelled) setCounts({ slack: d.slack || 0, teams: d.teams || 0 }); })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, []);

  return (
    <div className="min-h-screen bg-slate-950 text-white">
      <SEOHead
        title="CARTIE for Slack & Microsoft Teams · Ask your bill anything"
        description="Install CARTIE in Slack or Teams. Ask plain-English questions about your cloud and AI bill — get the cause, the file, and a pull request with the fix in under 10 seconds."
        path="/chat-bot"
      />
      <PublicNavbar />

      <main className="max-w-5xl mx-auto px-4 pt-24 pb-20">
        {/* Hero */}
        <header className="mb-12 text-center" data-testid="chat-bot-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/25 mb-5">
            <Sparkles className="w-3 h-3 text-emerald-300" />
            <span className="text-[11px] text-emerald-200 font-medium tracking-wide">Coming soon · join the waitlist · pick your platform</span>
          </div>
          <h1 className="text-3xl sm:text-5xl lg:text-6xl font-bold tracking-tight mb-4">
            Ask CARTIE in <span className="text-violet-300">Slack</span> or <span className="text-sky-300">Teams</span>
          </h1>
          <p className="text-base sm:text-lg text-slate-300 max-w-2xl mx-auto">
            Type a question about your cloud or AI bill in plain English. Get the cause, the file, and a pull request with the fix — in under 10 seconds. No dashboard. No new tool.
          </p>
        </header>

        {/* Live animated demo — answers "how does it actually work?" in 22 sec */}
        <section className="mb-12" data-testid="chat-bot-live-demo-section">
          <div className="text-center mb-5">
            <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">See it in action <span className="text-slate-500 font-normal">·</span> <span className="text-sky-300">Microsoft Teams</span></h2>
            <p className="text-sm text-slate-400">A real OpenAI cost spike, traced and fixed in under 30 seconds. Auto-plays.</p>
          </div>
          <LiveBotDemo platform="teams" />
        </section>

        {/* Two platform cards side by side — TEAMS FIRST (enterprise default) */}
        <section className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-14" data-testid="chat-bot-platforms">
          <PlatformCard platform="teams" count={counts.teams} />
          <PlatformCard platform="slack" count={counts.slack} />
        </section>

        {/* How it works — 4 plain-English steps */}
        <section className="mb-14" data-testid="chat-bot-how-it-works">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">How it works <span className="text-slate-500 font-normal">·</span> 4 steps, &lt; 5 minutes</h2>
          <p className="text-sm text-slate-400 mb-8 text-center">No new dashboard to learn. No agents to deploy. Just connect, watch, fix.</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3">
            <HowStep n={1} icon={Plug}            title="Connect"  body="Add Cartie to your Teams or Slack workspace in one click. We use read-only IAM roles on your cloud — never write access." accent="emerald" />
            <HowStep n={2} icon={Search}          title="Detect"   body="Cartie watches your cloud + AI spend every 60 seconds. When something costs more than expected, the bot pings the right channel." accent="sky" />
            <HowStep n={3} icon={GitPullRequest}  title="Diagnose" body="The bot traces every spike to the exact file and line of code. No more grep-the-logs. No more pager-duty hunts." accent="violet" />
            <HowStep n={4} icon={Bell}            title="Fix"      body="A pull request lands in your repo with the smallest possible patch. Your engineers review, click merge, done. CFO gets a Slack briefing in plain English." accent="amber" />
          </div>
        </section>

        {/* What you can ask */}
        <section className="mb-14" data-testid="chat-bot-examples">
          <h2 className="text-2xl font-bold text-white mb-2 text-center">What you can ask</h2>
          <p className="text-sm text-slate-400 mb-8 text-center">Real questions. Real answers. No keyword magic, no dashboards to learn.</p>
          <div className="space-y-3">
            {EXAMPLES.map((ex, i) => (
              <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-4" data-testid={`chat-bot-example-${i}`}>
                <div className="flex items-start gap-3 mb-2">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-white/5 text-[11px] font-mono text-slate-400 flex-shrink-0">You</span>
                  <p className="text-sm text-slate-200 font-medium">{ex.ask}</p>
                </div>
                <div className="flex items-start gap-3">
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-md bg-emerald-500/15 text-[11px] font-mono text-emerald-300 flex-shrink-0">AI</span>
                  <p className="text-sm text-slate-400 leading-relaxed">{ex.answer}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Trust strip */}
        <section className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-14" data-testid="chat-bot-trust">
          <Trust icon={Clock}        title="Under 10 seconds"      body="Most answers come back in 3-6 seconds. Built for the impatient." />
          <Trust icon={Lock}         title="Read-only by default"  body="The bot can answer questions. Any code change is always a PR you approve." />
          <Trust icon={CheckCircle2} title="Works in any channel"  body="Private DM with the CFO. Public channel with the engineering team. Up to you." />
        </section>

        {/* Soft CTA */}
        <section className="text-center pt-8 border-t border-white/5" data-testid="chat-bot-cta">
          <p className="text-slate-400 mb-5">In the meantime, while you wait for the bot —</p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link
              to="/cloud-bill-audit"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-white/5 hover:bg-white/10 text-white font-semibold border border-white/10 transition-colors"
            >
              Run a free cloud bill audit
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link
              to="/tokens"
              className="inline-flex items-center justify-center gap-2 px-5 py-3 rounded-xl bg-emerald-500 hover:bg-emerald-400 text-slate-950 font-semibold transition-colors"
            >
              See LLM pricing index
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}

const Trust = ({ icon: Icon, title, body }) => (
  <div className="p-4 rounded-xl border border-white/10 bg-white/[0.02]">
    <Icon className="w-4 h-4 text-emerald-300 mb-2" />
    <p className="text-sm font-semibold text-white mb-1">{title}</p>
    <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
  </div>
);

const STEP_ACCENT = {
  emerald: { bg: 'bg-emerald-500/15', ring: 'ring-emerald-500/30', text: 'text-emerald-300', glow: 'shadow-emerald-500/20' },
  sky:     { bg: 'bg-sky-500/15',     ring: 'ring-sky-500/30',     text: 'text-sky-300',     glow: 'shadow-sky-500/20' },
  violet:  { bg: 'bg-violet-500/15',  ring: 'ring-violet-500/30',  text: 'text-violet-300',  glow: 'shadow-violet-500/20' },
  amber:   { bg: 'bg-amber-500/15',   ring: 'ring-amber-500/30',   text: 'text-amber-300',   glow: 'shadow-amber-500/20' },
};

const HowStep = ({ n, icon: Icon, title, body, accent }) => {
  const a = STEP_ACCENT[accent];
  return (
    <div className="relative rounded-2xl border border-white/10 bg-white/[0.02] p-5 hover:bg-white/[0.04] transition-colors">
      <div className={`inline-flex items-center justify-center w-10 h-10 rounded-xl ${a.bg} ring-1 ${a.ring} shadow-lg ${a.glow} mb-3`}>
        <Icon className={`w-5 h-5 ${a.text}`} strokeWidth={2.2} />
      </div>
      <div className="text-[10px] font-bold uppercase tracking-widest text-slate-500 mb-1">Step {n}</div>
      <h3 className="text-base font-semibold text-white mb-1.5">{title}</h3>
      <p className="text-xs text-slate-400 leading-relaxed">{body}</p>
    </div>
  );
};
